// Replaces the /contact page's mailto: links for General Questions / Technical
// Support (DF-112, same pattern as dashboard/FeedbackModal.tsx).
//
// A mailto: link only works if the visitor has a local mail client configured
// -- silently a no-op for most mobile/webmail users, with zero signal that it
// failed. This writes the message to Firestore, which fans out to an HQ email
// via onContactMessageCreatedAdminEmail (DF-241) -- the original version only
// fired a PostHog event that nobody read, so "we read every one of these"
// was false for every visitor who used it. Still fires the PostHog event for
// analytics, and keeps a mailto fallback for anyone who wants to email direct.

import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { X } from 'lucide-react';
import { addDoc, collection } from 'firebase/firestore';
import Button from '@/components/ui/Button';
import { db } from '@/lib/firebase/config';
import { posthog } from '@/lib/analytics/posthog';

interface ContactCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  topic: 'general' | 'technical' | 'chat';
  title: string;
  description: string;
  email: string;
}

export default function ContactCaptureModal({
  isOpen,
  onClose,
  topic,
  title,
  description,
  email,
}: ContactCaptureModalProps) {
  const [message, setMessage] = useState('');
  const [replyEmail, setReplyEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  function handleClose() {
    setMessage('');
    setReplyEmail('');
    setSubmitted(false);
    onClose();
  }

  function submit() {
    if (!message.trim()) return;
    try {
      posthog.capture('contact_form_submit', {
        topic,
        message: message.trim(),
        reply_email: replyEmail.trim() || null,
      });
    } catch {
      /* posthog not initialised -- nothing to record */
    }
    addDoc(collection(db, 'contactMessages'), {
      topic,
      message: message.trim(),
      replyEmail: replyEmail.trim() || null,
      pageUrl: window.location.href,
      createdAt: Date.now(),
    }).catch((err) => console.error('[ContactCaptureModal] Failed to save message', err));
    setSubmitted(true);
    window.setTimeout(handleClose, 1800);
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-[110]">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-sm rounded-lg bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-lg font-semibold">{title}</Dialog.Title>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>

          {submitted ? (
            <p className="text-sm text-gray-600">Thanks — we read every one of these. 🙏</p>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">{description}</p>
              <textarea
                autoFocus
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="What's on your mind?"
              />
              <input
                type="email"
                value={replyEmail}
                onChange={(e) => setReplyEmail(e.target.value)}
                className="mt-2 w-full rounded-md border border-gray-300 p-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="Your email (optional, if you want a reply)"
              />
              <div className="mt-4 flex items-center justify-between">
                <a
                  href={`mailto:${email}`}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Prefer email? {email}
                </a>
                <Button onClick={submit} disabled={!message.trim()} size="sm">
                  Send
                </Button>
              </div>
            </>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
